<script lang="ts">
export default {
  name: 'PolicyDeleteModal',
};
</script>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import AppModal from './v2/AppModal.vue';
import { accountService } from '@/services/api';
import { documentService } from '@/services/v2/DocumentService';
import { Document } from '@/types/v2/Document';
import { computed, ref, watch } from 'vue';
import { relativeDateTime } from '@/utilities/Dates';
import { IsUserAClient } from '@/utilities/AuthUtils';
import Store from '@/store';
import { formatDate } from '@/utilities/Format';
import { Users } from '@lucide/vue';
import { useToast } from '@/composables/useToast';

const $route = useRoute();
const toast = useToast();

const props = defineProps<{
  policyNumber: string;
  accountId: number;
  policyId: number;
  show?: boolean;
}>();

const emit = defineEmits<{
  'needs-update': [boolean];
  closed: [];
}>();

// -----
// refs
// -----
const documents = ref([] as Partial<Document>[]);
const confirmDocsDeletion = ref(false);
const showModal = ref(false);
const loading = ref(false);

// Watch for external show prop changes
watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      handleOpenModal();
    }
  },
);

// ---------
// computed
// ---------
const isClientRole = computed<boolean>(() => {
  return IsUserAClient(Store.state.user);
});

const hasAnyDocuments = computed<boolean>(() => {
  return documents.value.length > 0;
});

const modalText = computed<string>(() => {
  return hasAnyDocuments.value
    ? 'Are you sure you want to delete this policy and all its associated documents permanently?'
    : 'Are you sure you want to delete this policy?';
});

// --------
// methods
// --------
async function handleOpenModal() {
  showModal.value = true;
  confirmDocsDeletion.value = false;
  await loadDocuments();
}

async function handleCloseModal() {
  showModal.value = false;
  confirmDocsDeletion.value = false;
  documents.value = [];
  emit('closed');
}

function isGoneStatus(error: unknown): boolean {
  const status = (error as { cause?: { status?: number } })?.cause?.status;

  return status === 404 || status === 410;
}

async function handleConfirmDelete() {
  if (!confirmDocsDeletion.value) {
    return;
  }

  loading.value = true;
  try {
    await accountService.deletePolicy($route.params.id as string, props.policyNumber);
    emit('needs-update', true);
    showModal.value = false;
    emit('closed');
  } catch (error) {
    console.error('Failed to delete policy:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to delete the policy.');

    // 404 means the policy is already gone, so the list on screen is stale and worth refetching,
    // and there is nothing left to retry. Every other failure leaves the policy in place: keep the
    // modal open so a transient error can be retried without re-confirming the document list.
    if (isGoneStatus(error)) {
      handleCloseModal();
      emit('needs-update', true);
    }
  } finally {
    loading.value = false;
  }
}

async function loadDocuments(): Promise<void> {
  documents.value = await documentService.getDocumentsByPolicy(props.accountId, props.policyId);
  if (documents.value.length === 0) {
    confirmDocsDeletion.value = true;
  }
}
</script>

<template>
  <slot
    name="button"
    v-bind="{ onClick: handleOpenModal }">
  </slot>

  <AppModal
    title="Delete Policy"
    :show="showModal"
    :handle-cancel="handleCloseModal"
    :handle-action="handleConfirmDelete"
    action-button-label="Delete"
    :loading="loading"
    :action-disabled="!confirmDocsDeletion || loading"
    size="lg">
    <template #content>
      <div class="flex flex-col gap-4">
        <span class="text-xl text-primary">
          {{ modalText }}
        </span>
        <!-- existing documents for policy -->
        <table
          v-if="documents.length > 0"
          class="table table-xs">
          <thead>
            <tr class="border-b border-solid border-gray-200">
              <th>Name</th>
              <th v-show="!isClientRole"><!-- published status --></th>
              <th>Type</th>
              <th>Uploaded</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="doc in documents"
              :key="doc.documentId">
              <td>{{ doc.displayName }}</td>
              <th v-show="!isClientRole">
                <div
                  v-if="doc.published"
                  data-tip="This document is published and viewable by client users"
                  class="tooltip tooltip-right top-0.5 flex justify-end">
                  <Users
                    class="text-gray-500"
                    :size="16" />
                </div>
              </th>
              <td>{{ doc.documentTypeName }}</td>
              <td class="flex flex-col items-start justify-center">
                <span>{{ relativeDateTime(doc.createdByDate) }}</span>
                <span class="text-xs text-gray-600">{{ formatDate(doc.createdByDate) }}</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Checkbox for confirmation -->
        <div
          v-if="hasAnyDocuments"
          class="flex gap-2">
          <input
            type="checkbox"
            id="confirmDeletion"
            v-model="confirmDocsDeletion"
            class="checkbox border border-solid border-gray-400 checkbox-sm checkbox-primary" />
          <label for="confirmDeletion"> I understand that the policy and all documents listed above will be permanently deleted. </label>
        </div>
      </div>
    </template>
  </AppModal>
</template>
